import React from "react";
import { LogOut, Trophy, Wallet, LayoutDashboard } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import partnersLogo from "@/assets/partners_logo.png";

const awesomeSerif = { fontFamily: "AwesomeSerif, serif" };

interface User {
  user_metadata?: {
    name?: string;
    socialMedia?: string;
  };
}

interface DashboardHeaderProps {
  user: User | null;
  onLogout: () => void;
  onNavigateToRanking: () => void;
  onNavigateToWallet: () => void;
  onNavigateToDashboard: () => void;
  activeTab: string;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  user,
  onLogout,
  onNavigateToRanking,
  onNavigateToWallet,
  onNavigateToDashboard,
  activeTab,
}) => {
  // Extract first name from full name
  const getFirstName = (fullName?: string) => {
    if (!fullName) return "Influenciador";
    return fullName.split(" ")[0];
  };

  const firstName = getFirstName(user?.user_metadata?.name);

  return (
    <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top row: logo + actions */}
        <div className="flex justify-between items-center py-4 sm:py-6">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
              <AvatarImage src={partnersLogo} alt="Silva Partners" />
              <AvatarFallback>SP</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-lg sm:text-2xl text-foreground" style={awesomeSerif}>Silva Partners</h1>
              <p className="text-muted-foreground text-xs sm:text-sm">
                Bem-vindo, <span className="text-primary font-medium">{firstName}</span>!
              </p>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-3">
            <button
              onClick={onNavigateToDashboard}
              className={`flex items-center gap-2 text-base font-medium transition-colors ${activeTab === "dashboard" ? "text-primary" : "text-foreground hover:text-primary"}`}
            >
              <LayoutDashboard className="h-5 w-5" />
              Dashboard
            </button>
            <span className="text-muted-foreground/20 text-lg">|</span>
            <button
              onClick={onNavigateToRanking}
              className={`flex items-center gap-2 text-base font-medium transition-colors ${activeTab === "public-ranking" ? "text-primary" : "text-foreground hover:text-primary"}`}
            >
              <Trophy className="h-5 w-5" />
              Ranking
            </button>
            <span className="text-muted-foreground/20 text-lg">|</span>
            <button
              onClick={onNavigateToWallet}
              className={`flex items-center gap-2 text-base font-medium transition-colors ${activeTab === "withdrawal-requests" ? "text-primary" : "text-foreground hover:text-primary"}`}
            >
              <Wallet className="h-5 w-5" />
              Carteira
            </button>
            <span className="text-muted-foreground/20 text-lg">|</span>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-base font-medium text-foreground hover:text-primary transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Sair
            </button>
          </div>

          {/* Mobile nav */}
          <div className="flex sm:hidden items-center gap-3">
            <button onClick={onNavigateToDashboard} className={`transition-colors ${activeTab === "dashboard" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <LayoutDashboard className="h-5 w-5" />
            </button>
            <button onClick={onNavigateToRanking} className={`transition-colors ${activeTab === "public-ranking" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <Trophy className="h-5 w-5" />
            </button>
            <button onClick={onNavigateToWallet} className={`transition-colors ${activeTab === "withdrawal-requests" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <Wallet className="h-5 w-5" />
            </button>
            <button onClick={onLogout} className="text-muted-foreground hover:text-primary transition-colors">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
